import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useMenuCatalog } from './transaction-editor/useMenuCatalog';
import {
  addSelectedSetLine,
  calculateSelectedItemsPaymentTotal,
  calculateSelectedItemsServiceCharge,
  removeLastSelectedLine,
  updateSelectedMenuItems,
} from './transaction-editor/useBulkSettlementTransactionCards';
import { menuItemDetails } from '../../../../services/table.services';
import { buildSetMenuAddons, calculateSetMenuPrice } from '../../../../utils/setMenuAddons';
// The set picker is the qr diner's component, mounted into a SweetAlert — the
// order screen opens the same one.
import { openSetMenuPicker } from '../../../../components/setMenuPickerDialog';
import { rowsToSetSelections } from '../../../../utils/setPickerRows';
import { ITEM_TYPES } from '../../../../../../utils/Constants';
import type {
  BulkSettlementMenuItem,
  BulkSettlementSelectedMenuItem,
  BulkSettlementTransactionCardItem,
} from './types';

type LocationState = {
  businessDate?: string;
  cardId?: string;
  cards?: BulkSettlementTransactionCardItem[];
  selectedItems?: BulkSettlementSelectedMenuItem[];
  serviceChargePercentage?: number;
};

const showDiscardConfirm = (title: string, text: string, confirmText: string, cancelText: string) =>
  Swal.fire({
    title,
    text,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#6c757d',
    cancelButtonColor: '#dc3545',
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    customClass: {
      popup: 'swal-restaurant',
      confirmButton: 'btn btn-secondary col',
      cancelButton: 'btn btn-danger col',
    },
  } as any);

const BulkSettlementMenuItemsPage = () => {
  const { t, i18n } = useTranslation();
  const catName = (cat: { name: string; nameJa?: string }) =>
    i18n.language === 'en' ? cat.name : (cat.nameJa || cat.name);
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;
  const businessDate = state.businessDate;
  const cardId = state.cardId || '';
  const serviceChargePercentage = state.serviceChargePercentage || 0;
  const restoredCards = state.cards;

  const initialItems = useMemo(() => state.selectedItems || [], []);

  const { activeCategoryId, loadMenuCategories, loading, menuCategories, setActiveCategoryId } =
    useMenuCatalog();
  const [selectedItems, setSelectedItems] = useState<BulkSettlementSelectedMenuItem[]>(
    initialItems,
  );

  const hasChanges = JSON.stringify(selectedItems) !== JSON.stringify(initialItems);
  const transactionHasData = (restoredCards || []).length > 1 ||
    (restoredCards || []).some(
      card =>
        (card.total !== '' && Number(card.total) > 0) ||
        card.selectedItems.length > 0 ||
        card.paymentMethod !== 'cash',
    );
  const shouldWarn = hasChanges || transactionHasData;
  const intentionalNavRef = useRef(false);

  useEffect(() => {
    loadMenuCategories();
  }, []);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !intentionalNavRef.current &&
      shouldWarn &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    showDiscardConfirm(
      t('msg_are_you_sure'),
      t('label_bulk_settlement_discard_confirm'),
      t('btn_yes'),
      t('btn_cancel'),
    ).then(result => {
      if (result.dismiss === Swal.DismissReason.close) return;
      try {
        if (result.isConfirmed) blocker.proceed();
        else blocker.reset();
      } catch (_) {}
    });
  }, [blocker.state]);

  useEffect(() => {
    if (!shouldWarn) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [shouldWarn]);

  /**
   * A set item needs its components chosen before it can be recorded, so every
   * plus opens the picker and every confirmed pick becomes its own line. Two
   * sets with different curries are two lines at two prices — the same shape the
   * order screen produces, and what the server prices from the (group, item)
   * pairs.
   *
   * No spice: this records a past sale and prints no kitchen ticket.
   */
  const addSetItem = async (item: BulkSettlementMenuItem) => {
    let groups: any[] = [];
    let detailItem: any = { ...item };
    try {
      const detailRes = await menuItemDetails({ o_id: item._id });
      groups = detailRes?.data?.addons || [];
      if (detailRes?.data) detailItem = { ...item, ...detailRes.data };
    } catch (err) {
      console.error(err);
      return;
    }
    const rows = await openSetMenuPicker({
      item: detailItem,
      groups,
      // No spice: this records a past sale and prints no kitchen ticket, so
      // there is no heat to cook to and nothing to ask.
      withSpice: false,
      incompleteMessage: t('select_item_text'),
      soldOutMessage: t('sold_out'),
      swalOptions: {
        // Same width as the order screen's picker — an option row holds a name,
        // a price and a stepper, which the default 32rem cannot fit.
        width: 'min(66rem, 94vw)',
        showCancelButton: true,
        // No `confirmButtonText`: the picker writes its own "Add : ¥N" from the
        // running total, so anything set here would be overwritten on open.
        cancelButtonText: t('btn_cancel'),
        customClass: {
          // `swal-set-picker` lifts `.swal-restaurant`'s 435px cap, without
          // which the width above does nothing.
          popup: 'swal-restaurant swal-set-picker',
          confirmButton: 'btn btn-primary col',
          cancelButton: 'btn btn-secondary col',
        },
      },
    });

    if (!rows) return;
    const selections = rowsToSetSelections(rows, groups);
    const addons = buildSetMenuAddons(selections.groups, selections.selectionsByIndex, null);
    // A set that offered groups but produced no component is a failed selection,
    // not a bare line.
    if (groups.length && !addons.length) return;
    const priced = calculateSetMenuPrice(detailItem, addons, undefined);

    setSelectedItems(current =>
      addSelectedSetLine({
        selectedItems: current,
        item,
        addons: addons.map((addon: any) => ({
          addonId: addon.addon_id,
          itemId: addon.item_id,
          itemName: addon.item_name,
          totalPrice: Number(addon.total_price) || 0,
        })),
        lineTotal: Number(priced.total_price) || 0,
      }),
    );
  };

  const syncItem = (item: BulkSettlementMenuItem, delta: number) => {
    if (item.type === ITEM_TYPES.SET_OF_ITEMS) {
      if (delta > 0) {
        addSetItem(item);
      } else {
        setSelectedItems(current => removeLastSelectedLine(current, item._id));
      }
      return;
    }
    setSelectedItems(current =>
      updateSelectedMenuItems({ selectedItems: current, item, quantityDelta: delta }),
    );
  };

  // A set contributes one line per configuration, so its count is how many lines
  // it holds rather than a single line's quantity.
  const getQuantity = (itemId: string): number =>
    selectedItems
      .filter(entry => entry.itemId === itemId)
      .reduce((count, entry) => count + entry.quantity, 0);

  const total = calculateSelectedItemsPaymentTotal(selectedItems, serviceChargePercentage);
  const serviceCharge = calculateSelectedItemsServiceCharge(selectedItems, serviceChargePercentage);
  const activeCategory = menuCategories.find(c => c.id === activeCategoryId);

  const buildReturnCards = (
    items: BulkSettlementSelectedMenuItem[],
  ): BulkSettlementTransactionCardItem[] | undefined => {
    if (!restoredCards) return undefined;
    return restoredCards.map(card => {
      if (card.id !== cardId) return card;
      const cardTotal = calculateSelectedItemsPaymentTotal(items, serviceChargePercentage);
      return { ...card, selectedItems: items, total: cardTotal > 0 ? cardTotal.toFixed(2) : '' };
    });
  };

  const handleBack = () => {
    intentionalNavRef.current = true;
    navigate('/cashier/settings/bulk-settlement/transactions', {
      replace: true,
      state: { businessDate, restoredCards: buildReturnCards(selectedItems) },
    });
  };

  const handleDiscard = () => {
    showDiscardConfirm(
      t('msg_are_you_sure'),
      t('label_bulk_settlement_discard_confirm'),
      t('btn_yes'),
      t('btn_cancel'),
    ).then(result => { if (result.isConfirmed) setSelectedItems([]); });
  };

  const handleConfirm = () => {
    intentionalNavRef.current = true;
    navigate('/cashier/settings/bulk-settlement/transactions', {
      replace: true,
      state: { businessDate, restoredCards: buildReturnCards(selectedItems) },
    });
  };

  return (
    <div className="cashier-heading-innner">
      <div className="card-outer-header position-relative d-flex justify-content-center align-items-center mb-4">
        <button type="button" className="btn btn-icon position-absolute start-0" onClick={handleBack}>
          <i className="ti ti-arrow-left" />
        </button>
        <h4 className="heading-title-text mb-0">{t('input_label_menu_items')}</h4>
      </div>

      <div className="dashboard-content-wraper">
        <div
          className="card"
          style={{ borderRadius: '10px', overflow: 'hidden', height: 'calc(100vh - 270px)' }}
        >
          <div className="card-body p-0 d-flex flex-column" style={{ height: '100%' }}>
            <div
              className="border-bottom d-flex overflow-auto"
              style={{ flexShrink: 0, padding: '10px 15px', gap: '8px' }}
            >
              {loading ? (
                <div className="p-2 text-muted">Loading…</div>
              ) : (
                menuCategories.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setActiveCategoryId(cat.id)}
                    style={{
                      whiteSpace: 'nowrap',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      border: cat.id === activeCategoryId ? '1px solid #ff9500' : '1px solid #dee2e6',
                      background: cat.id === activeCategoryId ? '#ff9500' : '#fff',
                      color: cat.id === activeCategoryId ? '#fff' : '#495057',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    {catName(cat)}
                  </button>
                ))
              )}
            </div>

            <div className="flex-grow-1 overflow-auto p-3">
              {!loading &&
                activeCategory?.items.map((item: BulkSettlementMenuItem) => {
                  const qty = getQuantity(item._id);
                  return (
                    <div
                      key={item._id}
                      className="d-flex justify-content-between align-items-center border-bottom py-2"
                    >
                      <div>
                        <div className="fw-medium">{item.item_name}</div>
                        <div className="text-muted small">
                          ¥ {Number(item.total_price ?? item.price ?? 0).toFixed(2)}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          disabled={qty === 0}
                          onClick={() => syncItem(item, -1)}
                          style={{
                            width: '28px', height: '28px', padding: 0, borderRadius: '4px',
                            border: qty === 0 ? '1px solid #dee2e6' : '1px solid #ff9500',
                            background: qty === 0 ? '#f8f9fa' : '#ff9500',
                            color: qty === 0 ? '#adb5bd' : '#fff',
                            cursor: qty === 0 ? 'default' : 'pointer',
                            fontSize: '16px', lineHeight: 1,
                          }}
                        >
                          -
                        </button>
                        <span style={{ minWidth: '1.5rem', textAlign: 'center' }}>{qty}</span>
                        <button
                          type="button"
                          onClick={() => syncItem(item, 1)}
                          style={{
                            width: '28px', height: '28px', padding: 0, borderRadius: '4px',
                            border: '1px solid #ff9500', background: '#ff9500',
                            color: '#fff', cursor: 'pointer', fontSize: '16px', lineHeight: 1,
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}

              {!loading && !activeCategory?.items.length && (
                <div className="text-center text-muted py-4">
                  {t('category.no_menu_item_found')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="card"
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '275px',
          right: '25px',
          zIndex: 10,
          borderRadius: '6px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
      >
        <div className="card-body d-flex justify-content-between align-items-center py-2">
          <div>
            {serviceChargePercentage > 0 && (
              <div className="text-muted small">
                {t('label_bulk_settlement_service_charge')}: ¥ {serviceCharge.toFixed(2)}
              </div>
            )}
            <div className="text-muted small">{t('label_total')}</div>
            <div className="fs-5 fw-bold">¥ {total.toFixed(2)}</div>
          </div>
          <div className="d-flex gap-3">
            <button
              type="button"
              className={selectedItems.length === 0 ? 'btn' : 'btn btn-danger'}
              style={
                selectedItems.length === 0
                  ? { backgroundColor: '#f0f0f0', borderColor: '#dee2e6', color: '#adb5bd' }
                  : {}
              }
              disabled={selectedItems.length === 0}
              onClick={handleDiscard}
            >
              {t('label_bulk_settlement_discard')}
            </button>
            <button
              type="button"
              className="btn text-white"
              style={{ backgroundColor: '#ff9500', borderColor: '#ff9500' }}
              disabled={selectedItems.length === 0}
              onClick={handleConfirm}
            >
              {t('label_confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkSettlementMenuItemsPage;
